import { useMutation } from '@apollo/client'
import { useAuthenticationStatus, useUserData } from '@nhost/react'
import { LoaderCircle } from 'lucide-react'
import { PropsWithChildren, useEffect, useRef } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import NotAuthenticated from './NotAuthenticated'
import { ENSURE_USER_PROFILE } from './api'
import { ROLE_CONFIG, type AppRole } from './roleConfig'

/** Ein zweiter Versuch nach dieser Wartezeit, falls der erste scheitert. */
const RETRY_DELAY_MS = 2000

type AuthGateProps = PropsWithChildren<{ role: AppRole }>

/**
 * Lässt nur angemeldete Konten mit genau dieser Rolle durch. Ohne Anmeldung geht es
 * zur Anmeldeseite der Rolle, mit falscher Rolle zum Hinweis (der meldet ab).
 */
export function AuthGate({ children, role }: AuthGateProps) {
  const location = useLocation()
  const { isLoading, isAuthenticated } = useAuthenticationStatus()
  const userData = useUserData()
  const profileEnsured = useRef(false)
  const [ensureUserProfile] = useMutation(ENSURE_USER_PROFILE)

  // Jedes angemeldete Konto bekommt seine Profilzeile, falls sie noch fehlt. Ein
  // Fehler bleibt im Log: die Nutzerin kann nichts dagegen tun, ein Toast beim
  // Anmelden würde sie nur verunsichern. Dafür gibt es einen zweiten Versuch, weil
  // der erste direkt nach dem Anmelden auf ein noch nicht gültiges Token treffen kann.
  useEffect(() => {
    const userId = userData?.id
    if (!isAuthenticated || !userId || profileEnsured.current) return

    profileEnsured.current = true
    let retryTimer: ReturnType<typeof setTimeout> | undefined

    ensureUserProfile({ variables: { userId } }).catch((error) => {
      console.error('Failed to ensure user profile, retrying:', error)
      retryTimer = setTimeout(() => {
        ensureUserProfile({ variables: { userId } }).catch((retryError) => {
          console.error('Failed to ensure user profile:', retryError)
        })
      }, RETRY_DELAY_MS)
    })

    return () => clearTimeout(retryTimer)
  }, [isAuthenticated, userData?.id, ensureUserProfile])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoaderCircle className="w-10 h-10 animate-spin text-slate-500" />
      </div>
    )
  }

  if (!isAuthenticated) {
    // Nhost haengt bei einer gescheiterten Anbieter-Anmeldung error und
    // errorDescription an die Rueckkehr-Adresse. Ohne das Weiterreichen landet die
    // Besucherin wortlos wieder im Anmeldeformular und haelt es fuer ein Neuladen.
    const incoming = new URLSearchParams(location.search)
    const forwarded = new URLSearchParams()
    for (const key of ['error', 'errorDescription']) {
      const value = incoming.get(key)
      if (value) forwarded.set(key, value)
    }
    const search = forwarded.toString()
    return (
      <Navigate
        to={{ pathname: ROLE_CONFIG[role].signInPath, search: search ? `?${search}` : '' }}
        state={{ from: location }}
        replace
      />
    )
  }

  if (userData?.defaultRole === role) {
    return <>{children}</>
  }

  return <NotAuthenticated />
}
